import { format, parse, isValid, compareAsc } from 'date-fns';
import { format as formatWithTz, zonedTimeToUtc } from 'date-fns-tz';

import { DATE_FORMAT_ISO_FNS, DATE_FORMAT_LONG_FNS } from '../constants/dateFormats';

interface IParseDate {
  date: string | Date;
  parseFormat: string;
}

interface IFormatDate {
  date: string | Date;
  dateFormat: string;
  parseFormat?: string;
}

interface ICompareDates {
  dateLeft: string | Date;
  dateRight: string | Date;
  parseFormat?: string;
}

export const parseDate = ({ date, parseFormat }: IParseDate): Date | undefined => {
  if (typeof date === 'number') {
    return undefined;
  }
  const newDate = new Date(date);
  const parsedDate = parse(date instanceof Date ? date.toISOString() : date, parseFormat, new Date());

  if (isValid(newDate) && compareDates({ dateLeft: newDate.toISOString(), dateRight: date, parseFormat }) === 0) {
    const parsedIsoDate = parse(new Date(date).toISOString(), parseFormat, new Date());

    if (parsedIsoDate.toString() !== 'Invalid Date') {
      return parsedIsoDate;
    }
  }
  if (
    isValid(newDate) &&
    (!isValid(parsedDate) || compareDates({ dateLeft: date, dateRight: parsedDate.toString(), parseFormat }) === 0)
  ) {
    return newDate;
  }
  if (parsedDate.toString() === 'Invalid Date') {
    return undefined;
  }

  return parsedDate;
};

export const formatDate = ({ date, dateFormat, parseFormat = DATE_FORMAT_LONG_FNS }: IFormatDate): string => {
  try {
    const newDate = parseDate({ date, parseFormat });

    if (newDate) {
      return format(newDate, dateFormat);
    }

    return '';
  } catch (err) {
    return '';
  }
};

export const getFormatedClientTime = (parseFormat = DATE_FORMAT_ISO_FNS): string => format(new Date(), parseFormat);

export const formatDateLocale = ({ date, dateFormat, parseFormat = DATE_FORMAT_LONG_FNS }: IFormatDate): string => {
  try {
    const newDate = parseDate({ date, parseFormat });

    if (newDate) {
      return formatWithTz(zonedTimeToUtc(newDate, 'UTC'), dateFormat, { timeZone: 'UTC' });
    }

    return '';
  } catch (err) {
    return '';
  }
};

const convertDateStringToArray = (arr: string): number[] =>
  arr
    .split('.')
    .map(item => Number(item))
    .reverse();

export const compareDates = ({
  dateLeft,
  dateRight,
  parseFormat = DATE_FORMAT_LONG_FNS,
}: ICompareDates): number | false => {
  try {
    if (typeof dateLeft === 'string' && typeof dateRight === 'string' && dateLeft && dateRight) {
      const leftDateArr = convertDateStringToArray(dateLeft);
      const rightDateArr = convertDateStringToArray(dateRight);

      if (leftDateArr.length === rightDateArr.length && parseFormat === DATE_FORMAT_LONG_FNS) {
        let resultNumber: number | undefined;

        leftDateArr.forEach((item, index) => {
          if (resultNumber === undefined) {
            if (item < rightDateArr[index]) {
              resultNumber = -1;
            }
            if (item > rightDateArr[index]) {
              resultNumber = 1;
            }
          }
        });
        if (resultNumber !== undefined) {
          return resultNumber;
        }
      }
    }
    let newDateLeft = new Date(dateLeft);

    let newDateRight = new Date(dateRight);

    if (parseFormat && !isValid(newDateLeft)) {
      newDateLeft = parse(dateLeft instanceof Date ? dateLeft.toISOString() : dateLeft, parseFormat, new Date());
    }

    if (parseFormat && !isValid(newDateRight)) {
      newDateRight = parse(dateRight instanceof Date ? dateRight.toISOString() : dateRight, parseFormat, new Date());
    }

    return compareAsc(newDateLeft, newDateRight);
  } catch (err) {
    return false;
  }
};

/**
 * Функция нужна для реализации следующей логики:
 * К дате добавить 3 месяца (дд.мм+3.гггг). Если мм+3 выходит за 12, то дд.мм+3-12.гггг+1.
 * Если полученная дата (дд) не существует в получаемом месяце (мм), то дд должна быть равна последнему дню в полученном месяце.
 * @param date
 * @param months
 */
export function addMonths(date: Date, months: number): Date {
  const newDate = new Date(date);

  const oldDateMonth = date.getMonth();

  newDate.setMonth(date.getMonth() + months);

  const newDateMonth = newDate.getMonth();

  if (oldDateMonth + months > 12 && oldDateMonth + months < 12 + newDateMonth) {
    return new Date(newDate.getFullYear(), newDateMonth, 0);
  }

  if (oldDateMonth + months < newDateMonth) {
    return new Date(date.getFullYear(), newDateMonth, 0);
  }

  return newDate;
}
